import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  createPromoItem,
  createPromoItemType,
  deletePromoItem,
  deletePromoItemType,
  promotionQueryKeys,
  updatePromoItem,
  updatePromoItemType,
} from '../api/promotion.api';
import type { PromoItemPayload, PromoItemTypePayload } from '../types/promotion.types';

interface SaveArgs<TPayload> {
  id: number | null;
  payload: TPayload;
}

function useInvalidatePromotion() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: promotionQueryKeys.all() });
  };
}

export function useSavePromoItemType(onSuccess?: () => void) {
  const invalidate = useInvalidatePromotion();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<PromoItemTypePayload>) =>
      id === null ? createPromoItemType(payload) : updatePromoItemType(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Item type created' : 'Item type updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the item type'),
  });
}

export function useDeletePromoItemType(onSuccess?: () => void) {
  const invalidate = useInvalidatePromotion();

  return useMutation({
    mutationFn: (id: number) => deletePromoItemType(id),
    onSuccess: () => {
      invalidate();
      toast.success('Item type removed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to remove the item type'),
  });
}

export function useSavePromoItem(onSuccess?: () => void) {
  const invalidate = useInvalidatePromotion();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<PromoItemPayload>) =>
      id === null ? createPromoItem(payload) : updatePromoItem(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Item created' : 'Item updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the item'),
  });
}

export function useDeletePromoItem(onSuccess?: () => void) {
  const invalidate = useInvalidatePromotion();

  return useMutation({
    mutationFn: (id: number) => deletePromoItem(id),
    onSuccess: () => {
      invalidate();
      toast.success('Item removed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to remove the item'),
  });
}
